import { request, requestData } from '@/lib/api-client';
import type { LoginResult, LoginSession, MeResult, Session, UserRole, Uuid } from './types';

/**
 * Step one. Email and password.
 *
 * Answers either a finished session or a challenge — check `mfaRequired` before
 * touching anything else. Admin roles are configured to require the second
 * step; customers never reach this panel and sign in with a phone code instead.
 */
export function login(body: { email: string; password: string }): Promise<LoginResult> {
  return requestData<LoginResult>('/auth/login', { method: 'POST', body, anonymous: true });
}

/**
 * Step two. The code from the email, with the ticket from step one.
 *
 * `mfaToken` is what proves the password was already accepted. Without it the
 * server refuses — otherwise this route would be a second, password-free way
 * in, and the factor would have replaced the password rather than joined it.
 */
export function loginVerify(body: { mfaToken: string; code: string }): Promise<LoginSession> {
  return requestData<LoginSession>('/auth/login/verify', {
    method: 'POST',
    body,
    anonymous: true,
  });
}

export function me(): Promise<MeResult> {
  return requestData<MeResult>('/auth/me');
}

export function refresh(refreshToken: string): Promise<Session> {
  return requestData<Session>('/auth/refresh', {
    method: 'POST',
    body: { refreshToken },
    anonymous: true,
  });
}

export function logout(refreshToken: string): Promise<void> {
  return request<void>('/auth/logout', { method: 'POST', body: { refreshToken }, anonymous: true });
}

export function logoutEverywhere(): Promise<{ revokedSessions: number }> {
  return requestData<{ revokedSessions: number }>('/auth/logout-all', { method: 'POST' });
}

export function setPassword(body: { userId: Uuid; password: string }): Promise<void> {
  return request<void>('/auth/password', { method: 'POST', body });
}

export function grantRole(userId: Uuid, role: UserRole): Promise<void> {
  return request<void>(`/users/${userId}/roles`, { method: 'POST', body: { role } });
}

export function revokeRole(userId: Uuid, role: UserRole): Promise<void> {
  return request<void>(`/users/${userId}/roles/${role}`, { method: 'DELETE' });
}
